import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import {
  ASSET_DRIVER_ASSIGNMENTS_CREATE,
  ASSET_DRIVER_ASSIGNMENTS_UPDATE,
  ASSET_DRIVER_ASSIGNMENTS_VIEW,
  LIVE_TRACKING_MAP_VIEW,
} from "../../permissions";
import type { FleetMonitoringRouteFactory } from "./fleetMonitoringRouteContext";
import { requirePermission } from "./requirePermission";

const AssetDriverAssignmentsScreen = lazyRouteComponent(
  () => import("../AssetDriverAssignmentsScreen"),
  "AssetDriverAssignmentsScreen",
);
const AssetDriverAssignmentCreateScreen = lazyRouteComponent(
  () => import("../AssetDriverAssignmentCreateScreen"),
  "AssetDriverAssignmentCreateScreen",
);
const AssetDriverAssignmentEditScreen = lazyRouteComponent(
  () => import("../AssetDriverAssignmentEditScreen"),
  "AssetDriverAssignmentEditScreen",
);
const AssetDriverAssignmentHistoryScreen = lazyRouteComponent(
  () => import("../AssetDriverAssignmentHistoryScreen"),
  "AssetDriverAssignmentHistoryScreen",
);
const AssetDriverAssignmentDetailScreen = lazyRouteComponent(
  () => import("../AssetDriverAssignmentDetailScreen"),
  "AssetDriverAssignmentDetailScreen",
);
const LiveTrackingMapScreen = lazyRouteComponent(
  () => import("../LiveTrackingMapScreen"),
  "LiveTrackingMapScreen",
);

export const createFleetMonitoringRoutes: FleetMonitoringRouteFactory = ({
  context,
  trackingRepository,
  trackingStream,
  driverAssignmentsRepository,
  alertHistory,
  cameras,
  gprs,
}) => {
  const assetDriversPath = `${context.basePath}/assignments/asset-drivers`;
  // basePath is a runtime string, so the template loses the literal type
  // TanStack Router needs to type params -- $assignmentId is guaranteed by
  // the path (same as core's createDriversRoutes).
  const assetDriverDetailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${assetDriversPath}/$assignmentId`,
    beforeLoad: requirePermission([ASSET_DRIVER_ASSIGNMENTS_VIEW]),
    component: function AssetDriverAssignmentDetailRouteScreen() {
      const { assignmentId } = assetDriverDetailRoute.useParams() as unknown as {
        assignmentId: string;
      };
      return createElement(AssetDriverAssignmentDetailScreen, {
        repository: driverAssignmentsRepository,
        basePath: assetDriversPath,
        assignmentId,
      });
    },
  });
  const assetDriverEditRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${assetDriversPath}/$assignmentId/edit`,
    beforeLoad: requirePermission([ASSET_DRIVER_ASSIGNMENTS_UPDATE]),
    component: function AssetDriverAssignmentEditRouteScreen() {
      const { assignmentId } = assetDriverEditRoute.useParams() as unknown as {
        assignmentId: string;
      };
      return createElement(AssetDriverAssignmentEditScreen, {
        repository: driverAssignmentsRepository,
        basePath: assetDriversPath,
        assignmentId,
      });
    },
  });

  // The supervisors and assets routes are registered separately by
  // createSupervisorAssignmentsRoutes / createAssetAssignmentsRoutes (see
  // fleetMonitoringFeature.ts).
  return [
    createRoute({
      getParentRoute: context.getParentRoute,
      path: assetDriversPath,
      beforeLoad: requirePermission([ASSET_DRIVER_ASSIGNMENTS_VIEW]),
      component: () =>
        createElement(AssetDriverAssignmentsScreen, {
          repository: driverAssignmentsRepository,
          basePath: assetDriversPath,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${assetDriversPath}/create`,
      beforeLoad: requirePermission([ASSET_DRIVER_ASSIGNMENTS_CREATE]),
      component: () =>
        createElement(AssetDriverAssignmentCreateScreen, {
          repository: driverAssignmentsRepository,
          basePath: assetDriversPath,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${assetDriversPath}/history`,
      beforeLoad: requirePermission([ASSET_DRIVER_ASSIGNMENTS_VIEW]),
      component: () =>
        createElement(AssetDriverAssignmentHistoryScreen, {
          repository: driverAssignmentsRepository,
          basePath: assetDriversPath,
        }),
    }),
    assetDriverDetailRoute,
    assetDriverEditRoute,
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/tracking`,
      beforeLoad: requirePermission([LIVE_TRACKING_MAP_VIEW]),
      component: () =>
        createElement(LiveTrackingMapScreen, {
          repository: trackingRepository,
          stream: trackingStream,
          alertHistory,
          cameras,
          gprs,
        }),
    }),
  ];
};
